/**
 * Country Flag Utility
 * 
 * Maps country names to flag emojis for display in the UI
 */

/**
 * Get flag emoji for a country name
 * 
 * @param countryName - Country name (e.g., "Netherlands", "Belgium")
 * @returns Flag emoji string or fallback globe emoji
 */
export function getCountryFlag(countryName: string | null | undefined): string {
  if (!countryName) {
    return "🌍" // Globe emoji as fallback
  }

  // Normalize country name (lowercase, trim)
  const normalized = countryName.toLowerCase().trim()

  // Country name to flag emoji mapping
  const countryFlags: Record<string, string> = {
    // European countries
    netherlands: "🇳🇱",
    belgium: "🇧🇪",
    france: "🇫🇷",
    spain: "🇪🇸",
    portugal: "🇵🇹",
    italy: "🇮🇹",
    germany: "🇩🇪",
    "united kingdom": "🇬🇧",
    uk: "🇬🇧",
    ireland: "🇮🇪",
    denmark: "🇩🇰",
    sweden: "🇸🇪",
    norway: "🇳🇴",
    finland: "🇫🇮",
    iceland: "🇮🇸",
    greece: "🇬🇷",
    croatia: "🇭🇷",
    poland: "🇵🇱",
    "czech republic": "🇨🇿",
    austria: "🇦🇹",
    switzerland: "🇨🇭",
    
    // North America
    "united states": "🇺🇸",
    "united states of america": "🇺🇸",
    usa: "🇺🇸",
    canada: "🇨🇦",
    mexico: "🇲🇽",
    
    // South America
    brazil: "🇧🇷",
    argentina: "🇦🇷",
    chile: "🇨🇱",
    peru: "🇵🇪",
    colombia: "🇨🇴",
    
    // Asia
    indonesia: "🇮🇩",
    philippines: "🇵🇭",
    japan: "🇯🇵",
    china: "🇨🇳",
    india: "🇮🇳",
    thailand: "🇹🇭",
    malaysia: "🇲🇾",
    singapore: "🇸🇬",
    vietnam: "🇻🇳",
    "sri lanka": "🇱🇰",
    maldives: "🇲🇻",
    
    // Oceania
    australia: "🇦🇺",
    "new zealand": "🇳🇿",
    fiji: "🇫🇯",
    tahiti: "🇵🇫",
    hawaii: "🇺🇸", // Hawaii is part of USA
    
    // Africa
    "south africa": "🇿🇦",
    morocco: "🇲🇦",
    egypt: "🇪🇬",
    senegal: "🇸🇳",
    
    // Middle East
    israel: "🇮🇱",
    lebanon: "🇱🇧",
  }

  // Direct match
  if (countryFlags[normalized]) {
    return countryFlags[normalized]
  }

  // Try partial match (e.g., "United States" contains "united states")
  for (const [key, flag] of Object.entries(countryFlags)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return flag
    }
  }

  // Fallback: return globe emoji
  return "🌍"
}

